import React, { useMemo, useState, useEffect, useRef } from 'react';
import { BaseLayout } from '../../layouts';
import { useParams } from 'react-router-dom';
import { LevelCard } from '../../features/programPage/LevelCard';
import PolIcon from '../../assets/icons/main/polCoin.svg';
import ListIcon from '../../assets/icons/dashboard/listicon.svg';
import GridIcon from '../../assets/icons/dashboard/gridIcon.svg';
import { useWeb3React } from '@web3-react/core';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { updateMatrixX2X4 } from '../../store/matrixX2X4Slice';
import { GET_MATRIX_DATA } from '../../helpers/graphRequests';
import { useQuery } from '@apollo/client';
import { getUser } from '../../store/userSlice/selectors';
import { getMatrixX2X4 } from '../../store/matrixX2X4Slice/selectors';
import { PROGRAM_NAMES, getLevelPriceWithCommission, getLevelPriceWithWeekly } from '../../helpers/constants';
import { ProgramsDropdown } from '../../components/ProgramsDropdown';
import { NftBanner } from '../../features/programPage/NftBanner';
import { usePreviewMode } from '../../helpers/hooks/useCheckPreviewMode';
import { useUpgradeLvl } from '../../helpers/hooks/useUpgradeLvl';
import { useCallTransaction } from '../../helpers/hooks/useCallTransaction';
import { Button } from '../../components/Button';
import { round } from '../../helpers/numbers';
import { usePassiveIncome } from '../../helpers/hooks/usePassiveIncome';
import { BuyWithOptionals } from '../../components/Modals/BuyWithOptionals';
import { useOnchainActiveLevels } from '../../helpers/hooks/useOnchainActiveLevels';

export const Program = () => {
  const { account } = useWeb3React();
  const dispatch = useDispatch();
  let { program } = useParams();
  const { previewUserAddress } = usePreviewMode();
  const [userAddress, setUserAddress] = useState(null);

  useEffect(() => {
    if (previewUserAddress) {
      setUserAddress(previewUserAddress?.toLocaleLowerCase());
    } else {
      setUserAddress(account?.toLocaleLowerCase());
    }
  }, [account, previewUserAddress]);
  const [isNeedToCall, setIsNeedToCall] = useState(false);
  const [isFirstCall, setIsFirstCall] = useState(true);

  const { x2: onchainX2, x4: onchainX4, refresh: refreshOnchainLevels } = useOnchainActiveLevels(userAddress);

  useEffect(() => {
    if (userAddress) {
      setIsNeedToCall(true);
      setIsFirstCall(false);
    }
  }, [userAddress]);

  const updateInfo = () => {
    if (!isFirstCall) {
      setIsNeedToCall(true);
    }
    refreshOnchainLevels();
  };

  const currentUser = useSelector(getUser);

  const matrixData = useSelector(getMatrixX2X4);

  const { data, error, loading, called } = useQuery(GET_MATRIX_DATA, {
    variables: { user: userAddress },
    skip: !isNeedToCall,
    fetchPolicy: 'cache-and-network',
  });

  const currentMatrix = useMemo(() => {
    if (program.toLocaleLowerCase() === PROGRAM_NAMES.X2.toLocaleLowerCase()) {
      return matrixData?.levelsX2;
    } else {
      return matrixData?.levelsX4;
    }
  }, [matrixData, program]);

  const currentTotalReward = useMemo(() => {
    if (program.toLocaleLowerCase() === PROGRAM_NAMES.X2.toLocaleLowerCase()) {
      return Number(currentUser?.totalRewardX2) / 1e18;
    } else {
      return Number(currentUser?.totalRewardX4) / 1e18;
    }
  }, [currentUser, program]);

  const currentMatrixName = useMemo(() => {
    if (program.toLocaleLowerCase() === PROGRAM_NAMES.X2.toLocaleLowerCase()) {
      return PROGRAM_NAMES.X2;
    } else {
      return PROGRAM_NAMES.X4;
    }
  }, [program]);

  const onchainMaxActive = currentMatrixName === PROGRAM_NAMES.X2 ? onchainX2 : onchainX4;
  const maxActiveLevel = onchainMaxActive ?? 0;
  const isLevelsReady = onchainMaxActive !== null;

  useEffect(() => {
    if (!data?.user?.levelsX2 && !data?.user?.levelsX4) {
      return;
    }

    dispatch(
      updateMatrixX2X4({
        loading,
        called,
        levelsX2: data.user.levelsX2,
        levelsX4: data.user.levelsX4,
      }),
    );
  }, [data, isNeedToCall, loading, called, dispatch]);

  const { getPassiveData, passiveData, distributionTime } = usePassiveIncome();

  useEffect(() => {
    if (account) {
      getPassiveData(null);
    }
  }, [account]);

  useEffect(() => {
    if (account && !passiveData) {
      getPassiveData(account);
    }
  }, [account, passiveData]);

  const updatePassiveData = () => {
    if (account) {
      getPassiveData(account);
    }
  };

  const displaySheet = [
    {
      icon: GridIcon,
    },
    {
      icon: ListIcon,
    },
  ];

  const [currenTub, setCurrentTub] = useState(0);

  // click to upgrade

  const levelsX2 = currentMatrixName === PROGRAM_NAMES.X2 ? [maxActiveLevel + 1] : [];
  const levelsX4 = currentMatrixName === PROGRAM_NAMES.X4 ? [maxActiveLevel + 1] : [];
  // const lastIncomeLevel =
  //   currentMatrixName === PROGRAM_NAMES.X2 ? matrixData?.x2IncomeLevel : matrixData?.x4IncomeLevel;

  const lastIncomeLevel =
    currentMatrixName === PROGRAM_NAMES.X2 ? passiveData?.x2?.lastActiveLevel : passiveData?.x4?.lastActiveLevel;

  const { upgradeLvl } = useUpgradeLvl();
  const { onCallTransaction, transactionInfo, resetTransactionInfo } = useCallTransaction();
  const [isBuyModalOpened, setIsBuyModalOpened] = useState(false);

  const isAllowToBuyWithWeekly = useMemo(() => {
    return Number(lastIncomeLevel) === Number(maxActiveLevel) && maxActiveLevel > 0;
  }, [lastIncomeLevel, maxActiveLevel]);

  const upgradeClick = async (activateIncome = null) => {
    try {
      const shouldActivateIncome = activateIncome !== null ? activateIncome : isAllowToBuyWithWeekly;
      const result = await upgradeLvl(levelsX2, levelsX4, shouldActivateIncome);

      if (result) {
        onCallTransaction(result);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleBuyClick = () => {
    setIsBuyModalOpened(true);
  };

  const handleBuyWithWeekly = () => {
    setIsBuyModalOpened(false);
    upgradeClick(true);
  };

  const handleBuyOnlyLevel = () => {
    setIsBuyModalOpened(false);
    upgradeClick(false);
  };

  useEffect(() => {
    if (transactionInfo.isSuccess) {
      updateInfo();
      resetTransactionInfo();
    }
  }, [transactionInfo]);

  return (
    <BaseLayout>
      <NftBanner mob={false} />
      <div className="flex flex-col w-full p-[4rem] relative rounded-[3rem] bg-[linear-gradient(0deg,rgba(255,255,255,0.04)_0%,rgba(255,255,255,0.04)_100%),#020204] border-[0.1rem] border-solid border-white-50 backdrop-blur-[5rem] lg:rounded-none lg:border-none lg:p-[1.6rem]">
        <div className="flex flex-col space-y-[3.2rem] w-full z-[11]">
          <div className="flex w-full justify-between space-y-0">
            <ProgramsDropdown currentMatrixName={currentMatrixName} />
            <div className="flex items-center space-x-[2.4rem] lg:space-x-0">
              <div className="flex items-center space-x-[0.8rem]">
                <span className="text-white text-[2rem] font-600 tracking-[-0.4px]">
                  {round(currentTotalReward)} POL
                </span>
                <img src={PolIcon} />
              </div>
              {/* <div className="flex items-center space-x-[1.2rem] lg:hidden">
                {displaySheet.map((item, itemIndex) => {
                  const isActive = currenTub === itemIndex;
                  return (
                    <button
                      onClick={() => setCurrentTub(itemIndex)}
                      className={`flex items-center justify-center w-[4rem] h-[4rem] border-[0.1rem] border-solid rounded-[1.2rem] custom-transition ${
                        isActive ? 'bg-white-100 border-white-300' : 'bg-white-50 border-white-50'
                      }`}
                      key={itemIndex}
                    >
                      <img className={`${!isActive && 'opacity-50'} custom-transition`} src={item?.icon} />
                    </button>
                  );
                })}
              </div> */}
            </div>
          </div>
          <NftBanner mob={true} />
          <div className="flex flex-col lg:space-y-[2.4rem]">
            {isLevelsReady && maxActiveLevel < 15 && (
              <div className="hidden lg:flex flex-col space-y-[1.6rem] w-full justify-between items-center border-[0.1rem] border-dashed border-white-200 rounded-[2rem] p-[2rem]">
                <div className="flex items-center justify-between space-x-[1rem] w-full">
                  <span className="text-[1.6rem] text-white font-600 leading-[2.24rem]">
                    Level {maxActiveLevel + 1}
                  </span>
                  <div className="flex items-center justify-center bg-white-100 rounded-[2rem] pl-[0.8rem] pr-[0.5rem] h-[2.8rem] space-x-[0.8rem]">
                    <span className="text-[1.4rem] font-500 tracking-[-0.28px] text-white opacity-50 leading-none">
                      Price
                    </span>
                    <span className="text-white text-[1.4rem] font-500 tracking-[-0.28px] leading-none">
                      {getLevelPriceWithCommission(currentMatrixName, maxActiveLevel)}
                    </span>
                    <img className="w-[1.8rem] h-[1.8rem]" src={PolIcon} />
                  </div>
                </div>
                <Button
                  type="purple"
                  loading={transactionInfo.isWaiting}
                  loadingType="white"
                  disabled={transactionInfo.isWaiting || previewUserAddress || !isLevelsReady}
                  onClick={isAllowToBuyWithWeekly ? handleBuyClick : () => upgradeClick()}
                  className="w-full min-w-[8.6rem] rounded-[3rem] shadow-[0px_0px_40px_0px_rgba(152,90,255,0.30)] h-[3.6rem]"
                >
                  <div className="flex items-center justify-center flex-nowrap space-x-[0.4rem] text-[1.4rem] font-500 leading-[1.96rem] text-white">
                    <span>Activate for</span>
                    <div className="flex items-center justify-center">
                      {getLevelPriceWithCommission(currentMatrixName, maxActiveLevel)}{' '}
                      <img className="w-[1.6rem] h-[1.6rem] ml-[0.4rem]" src={PolIcon} />
                    </div>
                  </div>
                </Button>
              </div>
            )}
            {currenTub === 0 ? (
              <div className="grid grid-cols-1 gap-[2rem] w-full lg:gap-[1.2rem]">
                {currentMatrix?.map((item, itemIndex) => {
                  const levelNumber = Number(item?.levelNumber) || itemIndex + 1;
                  const isActivatedOnChain = isLevelsReady && levelNumber <= maxActiveLevel;
                  const isFirstAfterActive = isLevelsReady && levelNumber === maxActiveLevel + 1;
                  const isSecondAfterActive = isLevelsReady && levelNumber === maxActiveLevel + 2;

                  return (
                    <LevelCard
                      key={item?.id ?? item?.levelNumber ?? itemIndex}
                      setIsNeedToCall={() => updateInfo()}
                      {...item}
                      lvl={levelNumber}
                      itemIndex={levelNumber - 1}
                      type={currentMatrixName}
                      maxActiveLevel={maxActiveLevel}
                      lastIncomeLevel={lastIncomeLevel}
                      isActivated={isActivatedOnChain}
                      isFirstAfterActive={isFirstAfterActive}
                      isSecondAfterActive={isSecondAfterActive}
                    />
                  );
                })}
              </div>
            ) : (
              <span className="text-white text-5xl">Будет список</span>
            )}
          </div>
        </div>
        <img className="absolute top-0 z-[0] lg:hidden" src="/images/dashboard/topShadow.webp" />
        <img
          className="absolute top-[-2rem] left-0 z-[0] h-[85rem] hidden lg:flex w-full"
          src="/images/dashboard/topShadowMob.webp"
        />
      </div>
      <BuyWithOptionals
        isOpened={isBuyModalOpened}
        onClose={() => setIsBuyModalOpened(false)}
        onBuyWithWeekly={handleBuyWithWeekly}
        onBuyOnlyLevel={handleBuyOnlyLevel}
        totalPriceLevel={getLevelPriceWithCommission(currentMatrixName, maxActiveLevel)}
        totalPriceWithWeekly={getLevelPriceWithWeekly(currentMatrixName, maxActiveLevel)}
      />
    </BaseLayout>
  );
};
